import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Lock, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getResourceContent, unlockResources } from "@/lib/resources.functions";
import { getLockedResourcePreview } from "@/lib/locked-resources.functions";
import { supabase } from "@/integrations/supabase/client";
import type { Locale } from "@/lib/i18n";
import type { ResourceBlock, ResourceMeta } from "@/lib/resources";
import { resourceOutline, resourceSlug } from "@/lib/resources";

const STORAGE_KEY = "sis-kunskapsbank-access";

const COPY = {
  sv: {
    locked: "Låst material",
    unlockTitle: "Få tillgång till hela materialet",
    unlockText:
      "Fyll i din e-postadress så mejlar vi en personlig länk som öppnar hela kunskapsbanken – alla guider, checklistor och nummerlistor. Kostnadsfritt.",
    email: "E-postadress",
    name: "Namn (frivilligt)",
    newsletter: "Skicka mig även skolans nyhetsbrev (kan avslutas när som helst)",
    submit: "Skicka min länk",
    sending: "Skickar…",
    unlocked: "Materialet är upplåst.",
    linkSent: "Vi har mejlat en personlig länk som öppnar hela kunskapsbanken. Spara mejlet – då kommer du in igen när du vill, även på en annan enhet.",
    linkSentTitle: "Kolla din mejl",
    error: "Något gick fel. Försök igen om en stund.",
    inThis: "Det här ingår",
    consent:
      "Vi använder din adress för att ge dig tillgång till materialet och, om du vill, till nyhetsbrevet. Du kan avsluta när du vill.",
    loading: "Hämtar materialet…",
    minutes: "min läsning",
    forget: "Logga ut från kunskapsbanken",
  },
  en: {
    locked: "Locked resource",
    unlockTitle: "Get access to the full resource",
    unlockText:
      "Enter your email and we send you a personal link that opens the whole knowledge hub – every guide, checklist and contact list. Free of charge.",
    email: "Email address",
    name: "Name (optional)",
    newsletter: "Also send me the school newsletter (unsubscribe any time)",
    submit: "Send my link",
    sending: "Sending…",
    unlocked: "The resource is unlocked.",
    linkSent: "We have emailed you a personal link that opens the whole knowledge hub. Keep the email and you can get back in any time, also on another device.",
    linkSentTitle: "Check your inbox",
    error: "Something went wrong. Please try again shortly.",
    inThis: "What's included",
    consent:
      "We use your address to give you access and, if you choose, to send the newsletter. You can unsubscribe at any time.",
    loading: "Loading the resource…",
    minutes: "min read",
    forget: "Sign out of the knowledge hub",
  },
  es: {
    locked: "Contenido protegido",
    unlockTitle: "Accede a todo el contenido",
    unlockText: "Introduce tu correo y te enviaremos un enlace personal que abre todo el centro de recursos: guías, listas y teléfonos útiles. Es gratuito.",
    email: "Correo electrónico",
    name: "Nombre (opcional)",
    newsletter: "Enviarme también el boletín del colegio (puedo darme de baja cuando quiera)",
    submit: "Enviarme el enlace",
    sending: "Enviando…",
    unlocked: "El contenido está desbloqueado.",
    linkSent: "Te hemos enviado un enlace personal que abre todo el centro de recursos. Guarda el correo y podrás volver a entrar cuando quieras, también desde otro dispositivo.",
    linkSentTitle: "Revisa tu correo",
    error: "Algo ha salido mal. Inténtalo de nuevo en unos minutos.",
    inThis: "Qué incluye",
    consent: "Usamos tu dirección para darte acceso y, si lo eliges, enviarte el boletín. Puedes darte de baja cuando quieras.",
    loading: "Cargando el contenido…",
    minutes: "min de lectura",
    forget: "Salir del centro de recursos",
  },
} as const;

export function readAccessToken(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function useResourceAccess() {
  const [token, setToken] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setToken(readAccessToken());
    setReady(true);
  }, []);

  const save = (value: string) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignoreras – åtkomsten gäller då bara denna sida */
    }
    setToken(value);
  };

  const clear = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignoreras */
    }
    setToken("");
  };

  return { token, ready, save, clear };
}

export function UnlockForm({ locale, slug }: { locale: Locale; slug: string }) {
  const t = COPY[locale];
  const unlock = useServerFn(unlockResources);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [wantsNewsletter, setWantsNewsletter] = useState(false);
  const [company, setCompany] = useState("");
  const [linkSent, setLinkSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      unlock({ data: { email, name, locale, slug, wantsNewsletter, company } }),
    onSuccess: () => {
      setLinkSent(true);
      toast.success(t.linkSentTitle, { id: "unlock" });
    },
    onError: (e: Error) => toast.error(e.message || t.error, { id: "unlock" }),
  });

  if (linkSent) {
    return (
      <div className="rounded-xl border border-primary/25 bg-accent/40 p-6 sm:p-8">
        <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-primary uppercase">
          <Lock className="size-3.5" aria-hidden />
          {t.locked}
        </p>
        <h2 className="mt-3 text-2xl font-semibold">{t.linkSentTitle}</h2>
        <p className="mt-2 max-w-xl leading-relaxed text-muted-foreground">{t.linkSent}</p>
        <p className="mt-3 text-sm text-muted-foreground">{email}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!mutation.isPending) mutation.mutate();
      }}
      className="rounded-xl border border-primary/20 bg-accent/40 p-6 sm:p-8"
    >
      <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-primary uppercase">
        <Lock className="size-3.5" aria-hidden />
        {t.locked}
      </p>
      <h2 className="mt-3 text-2xl font-semibold">{t.unlockTitle}</h2>
      <p className="mt-2 max-w-xl leading-relaxed text-muted-foreground">{t.unlockText}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="unlock-email">{t.email}</Label>
          <Input
            id="unlock-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="unlock-name">{t.name}</Label>
          <Input
            id="unlock-name"
            value={name}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            className="h-11"
          />
        </div>
      </div>

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        className="hidden"
      />

      <div className="mt-5 flex items-start gap-3">
        <Checkbox
          id="unlock-newsletter"
          checked={wantsNewsletter}
          onCheckedChange={(v) => setWantsNewsletter(v === true)}
          className="mt-1"
        />
        <Label htmlFor="unlock-newsletter" className="text-sm leading-6 font-normal">
          {t.newsletter}
        </Label>
      </div>

      <Button type="submit" className="mt-6 h-11 w-full sm:w-auto" disabled={mutation.isPending}>
        {mutation.isPending ? t.sending : t.submit}
      </Button>

      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        {t.consent}
      </p>
    </form>
  );
}

export function ResourceBlocks({ blocks }: { blocks: ResourceBlock[] }) {
  return (
    <div className="space-y-6">
      {blocks.map((block, i) => {
        if (block.type === "h2") {
          return (
            <h2 key={i} className="pt-4 text-2xl font-semibold">
              {block.text}
            </h2>
          );
        }
        if (block.type === "p") {
          return (
            <p key={i} className="leading-relaxed text-foreground/90">
              {block.text}
            </p>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={i} className="list-disc space-y-2 pl-5 text-foreground/90">
              {block.items.map((item, j) => (
                <li key={j} className="leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === "steps") {
          return (
            <ol key={i} className="grid gap-3 sm:grid-cols-2">
              {block.items.map((step, j) => (
                <li key={j} className="rounded-xl border border-border bg-card p-4">
                  <p className="text-sm font-semibold text-primary">{step.title}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {step.text}
                  </p>
                </li>
              ))}
            </ol>
          );
        }
        if (block.type === "contacts") {
          return (
            <ul key={i} className="grid gap-3">
              {block.items.map((c, j) => (
                <li
                  key={j}
                  className="flex flex-col gap-1 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-start sm:gap-4"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold sm:w-64 sm:shrink-0">
                    <Phone className="size-4 shrink-0 text-primary" aria-hidden />
                    {c.name}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium">{c.value}</span>
                    {c.note ? (
                      <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                        {c.note}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p
            key={i}
            className="rounded-xl border border-border bg-sand p-4 text-sm leading-relaxed text-muted-foreground"
          >
            {block.text}
          </p>
        );
      })}
    </div>
  );
}

const ADMIN_COPY = {
  sv: {
    signedIn: "Adminvy · du är inloggad med behörighet till materialet",
    unlock: "Lås upp som admin",
    visitor: "Visa som besökare",
    unlockedNote: "Du ser innehållet utan e-postlåset",
    draft: "utkast, ej publicerat",
  },
  en: {
    signedIn: "Admin view · you are signed in with access to this resource",
    unlock: "Unlock as admin",
    visitor: "View as visitor",
    unlockedNote: "You are seeing the content without the email gate",
    draft: "draft, not published",
  },
  es: {
    signedIn: "Vista de administrador · has iniciado sesión con acceso al material",
    unlock: "Desbloquear como administrador",
    visitor: "Ver como visitante",
    unlockedNote: "Estás viendo el contenido sin el bloqueo por correo",
    draft: "borrador, sin publicar",
  },
} as const;

export function ResourceBody({ resource, locale }: { resource: ResourceMeta; locale: Locale }) {
  const t = COPY[locale];
  const a = ADMIN_COPY[locale];
  const { token, ready, clear } = useResourceAccess();
  const fetchContent = useServerFn(getResourceContent);
  const previewContent = useServerFn(getLockedResourcePreview);
  const slug = resourceSlug(resource, locale);
  const [hasSession, setHasSession] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);

  const adminKey = `sis-admin-unlock:${resource.slug}:${locale}`;

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setHasSession(Boolean(data.session));
    });
    try {
      if (window.sessionStorage.getItem(adminKey) === "1") setAdminUnlocked(true);
    } catch {
      /* ignoreras */
    }
    return () => {
      active = false;
    };
  }, [adminKey]);

  const query = useQuery({
    queryKey: ["resource", resource.slug, locale, token],
    queryFn: () => fetchContent({ data: { slug: resource.slug, locale, token } }),
    enabled: ready && token.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const preview = useQuery({
    queryKey: ["resource-preview", resource.slug, locale],
    queryFn: () => previewContent({ data: { resourceSlug: resource.slug, locale } }),
    enabled: hasSession,
    retry: false,
    staleTime: 60 * 1000,
  });

  const canAdminUnlock = preview.data?.allowed === true;
  const adminPreview = canAdminUnlock && adminUnlocked;
  const unlocked = query.data?.unlocked === true || adminPreview;
  const blocks = (adminPreview ? preview.data?.blocks : query.data?.blocks) ?? [];

  function toggleAdmin(next: boolean) {
    setAdminUnlocked(next);
    try {
      if (next) window.sessionStorage.setItem(adminKey, "1");
      else window.sessionStorage.removeItem(adminKey);
    } catch {
      /* ignoreras */
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-5 py-12 sm:py-16">
      {canAdminUnlock ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-accent/40 px-4 py-3 text-sm text-primary">
          <span className="min-w-0">
            {adminPreview ? a.unlockedNote : a.signedIn}
            {preview.data?.status === "draft" ? ` (${a.draft})` : ""}.
          </span>
          <Button
            type="button"
            size="sm"
            variant={adminPreview ? "outline" : "default"}
            onClick={() => toggleAdmin(!adminPreview)}
          >
            {adminPreview ? a.visitor : a.unlock}
          </Button>
        </div>
      ) : null}
      {!unlocked ? (

        <>
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
            <h2 className="text-lg font-semibold">{t.inThis}</h2>
            <ul className="mt-4 space-y-2.5">
              {resourceOutline(resource, locale).map((item) => (
                <li key={item} className="flex items-start gap-3 text-foreground/90">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          {ready && token && query.isLoading ? (
            <p className="text-sm text-muted-foreground">{t.loading}</p>
          ) : (
            <UnlockForm locale={locale} slug={slug} />
          )}
        </>
      ) : (
        <>
          <ResourceBlocks blocks={blocks as ResourceBlock[]} />
          {adminPreview ? null : <button
            type="button"
            onClick={clear}
            className="text-xs text-muted-foreground link-quiet hover:text-foreground"
          >
            {t.forget}
          </button>}
        </>
      )}
    </div>
  );
}
